import React, { useState, useEffect } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { useOrganization } from '../../contexts/OrganizationContext';
import { useSettingsModal } from '../../contexts/SettingsModalContext';
import { useApplication } from '../../contexts/ApplicationContext';
import { useIntegrationStatus } from '../../contexts/IntegrationStatusContext';
import { providerLabel } from '../../config/providers';

const IntegrationStatusBanner: React.FC = () => {
  const { currentApplication } = useApplication();
  const { currentOrganization } = useOrganization();
  const { openOrgSettings } = useSettingsModal();
  const { issues, refresh: refreshStatus } = useIntegrationStatus();
  const [bannerDismissedFor, setBannerDismissedFor] = useState<string | null>(null);

  useEffect(() => {
    setBannerDismissedFor(null);
  }, [currentApplication?.id]);

  const appProviders = new Set(
    (currentApplication?.repositories ?? [])
      .filter((r) => !r.is_public_demo_repo)
      .map((r) => r.provider)
      .filter(Boolean),
  );
  const affectedProviders = Array.from(issues.keys()).filter((p) => appProviders.has(p));
  const dismissed = bannerDismissedFor === currentApplication?.id;

  if (
    !affectedProviders.length ||
    !currentApplication ||
    !currentOrganization ||
    dismissed ||
    currentOrganization.is_demo
  ) {
    return null;
  }

  const integrationList = affectedProviders.map((p) => providerLabel(p)).join(', ');

  const handleReconnect = () => {
    refreshStatus();
    openOrgSettings(currentOrganization.id, 'integrations');
  };

  return (
    <div
      role="alert"
      aria-live="polite"
      className="sticky top-0 z-20 bg-orange-100 dark:bg-orange-900/20 border-b border-orange-200 dark:border-orange-800 px-4 py-2 shrink-0"
    >
      <div className="relative flex items-center justify-center max-w-7xl mx-auto">
        <div className="flex items-center gap-2 text-sm text-orange-800 dark:text-orange-200">
          <AlertTriangle size={14} className="text-orange-600 dark:text-orange-400 shrink-0" />
          <span>
            Required integrations for this application need reconnection:{' '}
            <span className="font-medium">{integrationList}</span>{' — '}
            <button
              onClick={handleReconnect}
              className="font-medium underline hover:no-underline transition-colors"
            >
              Reconnect
            </button>
          </span>
        </div>
        <button
          onClick={() => setBannerDismissedFor(currentApplication.id)}
          className="absolute right-0 text-orange-600 dark:text-orange-400 hover:text-orange-800 dark:hover:text-orange-200 transition-colors"
          aria-label="Dismiss"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
};

export default IntegrationStatusBanner;
